import {
  CloseModalButton,
  FormActionButtons
} from '@library/components/buttons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CREATE_MEMBER_NOTE_FAILURE } from '@bo-constants/member/memberNotes';
import { Modal } from '@library/components/modals/Modal';
import { TextArea } from '@library/components/inputs';
import { useMemberNoteActions } from '@bo-hooks/members';

export interface CreateMemberNoteModalProps {
  onClose: () => void;
}

export const CreateMemberNoteModal = ({
  onClose
}: CreateMemberNoteModalProps) => {
  const {
    createNoteActions: { onSubmit }
  } = useMemberNoteActions();

  const [isSaveDisabled, setIsSaveDisabled] = useState(false);
  const [noteDescription, setNoteDescription] = useState('');
  const [isSubmitError, setIsSubmitError] = useState<string | undefined>(
    undefined
  );
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const saveDisabled = useMemo(
    () => isSaveDisabled || !noteDescription,
    [isSaveDisabled, noteDescription]
  );

  const resetDefaultState = useCallback(() => {
    setNoteDescription('');
    setIsSaveDisabled(false);
    setIsSubmitError(undefined);
  }, []);

  const handleCancelModal = useCallback(() => {
    resetDefaultState();
    onClose();
  }, [onClose, resetDefaultState]);

  const handleSubmit = useCallback(async () => {
    setIsSaveDisabled(true);

    await onSubmit?.(noteDescription)
      .then(() => {
        resetDefaultState();
        handleCancelModal();
      })
      .catch((error: Error) =>
        setIsSubmitError(error?.message ?? CREATE_MEMBER_NOTE_FAILURE)
      );
  }, [handleCancelModal, noteDescription, onSubmit, resetDefaultState]);

  useEffect(() => {
    if (textAreaRef.current) {
      textAreaRef.current.focus();
    }
  }, []);

  return (
    <Modal isOpen onCancelModal={handleCancelModal}>
      <div className="bg-soFresh-110 border-soFresh-140 relative mx-auto w-[35rem] flex-col rounded-md border">
        <div className="text-soClean-100 border-soFresh-140 flex items-center justify-between rounded-t border-b border-solid px-6 py-4 font-medium">
          <p className={isSubmitError ? 'text-roses' : ''}>
            <span>Create Note</span>
            {!!isSubmitError && (
              <span className="pl-1">- {CREATE_MEMBER_NOTE_FAILURE}</span>
            )}
          </p>
          <CloseModalButton
            btnClassName="p-0"
            handleOnClick={onClose}
            labelClassName="text-soClean-180 size-6"
            useDefaultStyling={false}
          />
        </div>
        <div className="flex max-h-[70vh] flex-col gap-y-2 p-6">
          <TextArea
            className="bg-soFresh-120 border-soFresh-140 h-auto min-h-36 border"
            labelClassName="text-soClean-180 text-sm"
            labelName="Description"
            ref={textAreaRef}
            value={noteDescription}
            onValueChange={setNoteDescription}
          />
        </div>
        <FormActionButtons
          className="pr-6"
          primary={{ onClick: handleSubmit, disabled: saveDisabled }}
          secondary={{ onClick: handleCancelModal }}
        />
      </div>
    </Modal>
  );
};
